import {
  Button,
  ButtonSize,
  Checkbox,
  Hide,
  TextArea
} from "shared/components";
import { Question, Template } from "types/landlord/verificationTemplate";
import { Plus } from "untitledui-js-base";
import { ApplicantType, VerificationNames } from "shared/types";
import { useTemplateContext } from "../provider";
import { QuestionCard } from "../components/QuestionCard";
import { CheckComponent } from "../components/CheckComponent";
import { ConfigurableCard } from "../components/ConfigurableCard";
import { FC } from "react";

interface AdditionalInformationProps {
  toggleServiceEnabled?: () => void;
}

export const AdditionalInformation: FC<AdditionalInformationProps> = ({
  toggleServiceEnabled
}) => {
  const { setTemplate, readOnly, template, partnerType } = useTemplateContext();

  const additionalQuestions =
      template?.verification_schemas[partnerType][
        VerificationNames.RentalApplication
      ]?.additional_questions || [],
    askCustomer =
      template?.verification_schemas[partnerType][
        VerificationNames.RentalApplication
      ]?.["additional_questions_step_enabled"];

  const updateAdditionalQuestions = (newQuestions: Question[]) => {
    const updatedTemplate = { ...(template || {}) };
    if (
      updatedTemplate.verification_schemas?.[partnerType][
        VerificationNames.RentalApplication
      ]
    ) {
      // @ts-ignore -- weird TS error
      updatedTemplate.verification_schemas[partnerType][
        VerificationNames.RentalApplication
      ].additional_questions = newQuestions;
      setTemplate(updatedTemplate as Template);
    }
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      name: "",
      required: false,
      type: "string"
    };
    updateAdditionalQuestions([...additionalQuestions, newQuestion]);
  };

  const toggleRequired = (index: number) => {
    const newQuestions = [...additionalQuestions];
    newQuestions[index].required = !newQuestions[index].required;
    updateAdditionalQuestions(newQuestions);
  };

  const removeQuestion = (index: number) => {
    const newQuestions = [...additionalQuestions];
    newQuestions.splice(index, 1);
    updateAdditionalQuestions(newQuestions);
  };

  const customer =
    partnerType === ApplicantType.CoSigner ? "guarantor" : "applicant";

  return (
    <ConfigurableCard
      title="Additional questions"
      switchProps={{
        checked: Boolean(askCustomer),
        label: readOnly
          ? `${askCustomer ? "Ask" : "Don't ask"} ${customer} about additional questions`
          : `Ask ${customer} about additional questions`,
        onChange: () => toggleServiceEnabled?.()
      }}
    >
      <Hide if={readOnly && !askCustomer}>
        <div className="mt-4 space-y-4 pt-4 border-t border-alpha-40">
          {askCustomer ? (
            <>
              {additionalQuestions.map((question, index) => (
                <QuestionCard
                  key={index}
                  title={`Additional question ${index + 1}`}
                  onRemove={readOnly ? undefined : () => removeQuestion(index)}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      {readOnly ? (
                        <p className="text-sm text-content-300">
                          {question.name}
                        </p>
                      ) : (
                        <TextArea
                          autoExpand
                          id={`question-${index}`}
                          value={question.name}
                          onChange={(e) => {
                            const newQuestions = [...additionalQuestions];
                            newQuestions[index].name = e.target.value;
                            updateAdditionalQuestions(newQuestions);
                          }}
                        />
                      )}
                    </div>

                    <div className="mb-2">
                      {readOnly ? (
                        <CheckComponent
                          checked={question.required ? true : undefined}
                          label={
                            question.required ? (
                              "Required"
                            ) : (
                              <p className="text-content-200">Optional</p>
                            )
                          }
                        />
                      ) : (
                        <Checkbox
                          label="Required"
                          checked={question.required}
                          onChange={() => toggleRequired(index)}
                          disabled={readOnly}
                        />
                      )}
                    </div>
                  </div>
                </QuestionCard>
              ))}
              <Hide if={readOnly}>
                <Button onClick={addQuestion} Icon={Plus} size={ButtonSize.SM}>
                  Add additional question
                </Button>
              </Hide>
            </>
          ) : null}
        </div>
      </Hide>
    </ConfigurableCard>
  );
};
